import React from 'react';
import { connect } from 'react-redux';
import { withCookies } from 'react-cookie';
import _ from 'lodash';
import {Button, Input, ListScreen, Select} from '../components';
import {
    fetchAiCommandItems,
    cleanAiCommandItems,
    setUser,
    changeUserSetting,
    deleteAiCommandItem,
    deleteAiCommandItems,
    runAiCommandItem,
    runAiCommandItems,
    editAiCommandItem,
    editAiCommandItems,
    saveAiCommandItems,
} from '../actions';
import {
    __,
    formatAmount,
    formatCurrency,
    formatDate,
    getCurrency,
    isEmptyString,
    request,
    toNumber
} from '../functions';
import '../assets/styles/ai-command-items.css';
import {AI_FUNCTION_NAMES, AI_TRANSLATE_FUNCTION_SLUG, AI_TRANSLATE_INPUTS} from '../config';
import DeleteIcon from "@mui/icons-material/Delete";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CopyIcon from "@mui/icons-material/FileCopy";
import {Tooltip, Chip, Autocomplete, TextField} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import GetAppIcon from '@mui/icons-material/GetApp';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SaveIcon from '@mui/icons-material/Save';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import Editor from 'ckeditor5-build-full';

class AiCommandItemsScreen extends ListScreen {
    title = __('AI úloha');

    state = { ...ListScreen.state, ...{
        lightbox: {
            run: false,
            delete: false,
            edit: false,
            create: false,
            price: false,
            multidelete: false,
            multirun: false,
            multiexport: false,
            multisave: false,
            multiedit: false,
            save: false,
            multiattachment: false,
        },
    }};

    statuses = {
        draft: __('Čaká na spustenie'),
        waiting: __('Spracováva sa'),
        processing: __('Spracováva sa'),
        processed: __('Spracované'),
        error: __('Chyba'),
    };

    statusColors = {
        draft: '#b8b22c',
        waiting: '#de8327',
        processing: '#de8327',
        processed: '#459350',
        error: '#c24646',
    };

    saveFields = {
        title: __('Názov produktu'),
        short_description: __('Krátky popis produktu'),
        long_description: __('Dlhý popis produktu'),
        seo_title: __('SEO názov produktu'),
        seo_description: __('SEO popis produktu'),
    };

    excelColumns = { A: 'A', B: 'B', C: 'C', D: 'D', E: 'E', F: 'F', G: 'G', H: 'H', I: 'I', J: 'J', K: 'K', L: 'L', M: 'M', N: 'N', O: 'O', P: 'P', Q: 'Q', R: 'R', S: 'S', T: 'T', U: 'U', V: 'V', W: 'W', X: 'X', Y: 'Y', Z: 'Z' };
    csvColumns = { 0: '0', 1: '1', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9', 10: '10', 11: '11', 12: '12', 13: '13', 14: '14', 15: '15', 16: '16', 17: '17', 18: '18', 19: '19', 20: '20', 21: '21', 22: '22', 23: '23', 24: '24', 25: '25' };

    memoryCacheParametersForAi = null;

    async fetchList(page = 1, per_page = 0, filtered = {}, callback = null) {
        this.page = page;

        const { fetch, params } = this.props;

        // Nacitame zoznam
        await fetch(this, params.id, { page, per_page, filtered, callback });

        const { items } = this.props;

        const function_name = toNumber(items.command.def) > 0 && _.has(AI_FUNCTION_NAMES, items.command.name)
            ? AI_FUNCTION_NAMES[items.command.name]
            : items.command.name;

        this.title = `${__('AI úloha')} - ${function_name}`;
        this.setTitle(this.title);

        if (!_.isEmpty(items.filtered)) {
            // Nastavime filter
            this.setState({ filtered: items.filtered });
        } else {
            this.setState({ title: this.title });
        }

        this.afterFetchList();
    }

    isValidPermission() {
        return this.hasPermission('aicommands');
    }

    getTableName() {
        return 'ai-command-items';
    }

    getFilter() {
        const { items } = this.props;
        const item_type = this.getItemType();

        return _.reduce(items.filter, (result, options, key) => {
            switch (key) {
                case 'id':
                    return { ...result, ...{ [key]: {
                        type: 'input',
                        name: __('ID'),
                        value: '',
                    }}};

                case 'status':
                    return { ...result, ...{ [key]: {
                        type: 'select',
                        name: __('Stav'),
                        value: '',
                        options: this.statuses,
                    }}};

                case 'error_save':
                    if (item_type !== 'product') {
                        return result;
                    }

                    return { ...result, ...{ [key]: {
                        type: 'select',
                        name: __('Chyba pri uložení v eshope'),
                        value: '',
                        options: {
                            'null': __('Nie'),
                            'not_null': __('Áno'),
                        },
                    }}};

                case 'saved':
                    if (item_type !== 'product') {
                        return result;
                    }

                    return { ...result, ...{ [key]: {
                        type: 'select',
                        name: __('Uložené v eshope'),
                        value: '',
                        options: {
                            0: __('Nie'),
                            1: __('Áno'),
                        },
                        strict: false,
                    }}};

                default:
                    return result;
            }
        }, {})
    }

    getMultiselect() {
        const { items } = this.props;

        const item_type = this.getItemType();
        const isGenerateParametersFunction = this.isGenerateParametersFunction();

        const showMultiAttachment = items.attachment_extension !== '';

        return [
            {
                confirm: (ids, callback, callbackLoading) => this.showLightbox('multirun', {
                    ids,
                    callback,
                    callbackLoading,
                }),
                icon: <SmartToyIcon color="orange" />,
                text: __('Spustiť'),
                validation: item => !_.includes(['waiting', 'processing'], item.status),
            },
            {
                confirm: (ids, callback, callbackLoading) => this.showLightbox('multiedit', {
                    ids,
                    callback,
                    callbackLoading,
                    input: '{input}',
                    output: '{output}',
                }),
                icon: <EditIcon />,
                text: __('Upraviť'),
                validation: item => !_.includes(['waiting', 'processing'], item.status),
            },
            item_type === 'product' ? {
                confirm: (ids, callback, callbackLoading) => this.showLightbox('multisave', {
                    save_field: '',
                    meta_name: '',
                    ids,
                    callback,
                    callbackLoading,
                }),
                icon: <SaveIcon />,
                text: __('Uložiť do eshopu'),
                validation: item => item.status === 'processed' && item.output !== null && item.output !== '',
            } : null,
            showMultiAttachment ? {
                confirm: (ids, callback, callbackLoading) => this.showLightbox('multiattachment', {
                    ids,
                    callback,
                    callbackLoading,
                    settings: {
                        export_cell: '',
                    },
                }),
                icon: <ImportExportIcon />,
                text: __('Exportovať do importovaného súboru'),
            } : null,
            !isGenerateParametersFunction ? {
                confirm: (ids, callback, callbackLoading) => this.showLightbox('multiexport', {
                    ids,
                    callback,
                    callbackLoading,
                }),
                icon: <GetAppIcon />,
                text: __('Exportovať'),
            } : null,
            {
                confirm: (ids, callback, callbackLoading) => this.showLightbox('multidelete', {
                    ids,
                    callback,
                    callbackLoading,
                }),
                icon: <DeleteIcon />,
                text: __('Zmazať'),
                validation: item => !_.includes(['waiting', 'processing'], item.status),
            },
        ];
    }

    getColumns() {
        const item_type = this.getItemType();

        return [
            <div>{__('ID')}</div>,
            <div>{item_type === 'product' ? __('Produkt') : __('Typ')}</div>,
            <div>{__('Stav')}</div>,
            <div>{__('Vstup')}</div>,
            <div>{__('Výstup')}</div>,
            <div>{__('Cena')}</div>,
            <div></div>,
        ];
    }

    getCells(item) {
        const status = <span style={{ color: this.statusColors[item.status] }}>{this.statuses[item.status]}</span>;
        const isRunning = _.includes(['waiting', 'processing'], item.status);
        const isProcessed = item.status === 'processed';
        const isError = item.status === 'error';
        const isErrorSave = item.error_save !== '' && item.error_save !== null;
        const isSaved = toNumber(item.saved) === 1;

        let output = item.output;
        let outputTitle = output;
        let outputAdditional = null;
        let truncateOutput = true;

        const isGenerateParametersFunction = this.isGenerateParametersFunction();

        if (isError) {
            let error = item.error;

            switch (error) {
                case 'Empty credit':
                    error = __('Nedostatočný kredit');
                    break;
            }

            output = error;
            outputTitle = error;
        } else if (isGenerateParametersFunction) {
            // Reformat parameters
            const parameters = this.getParametersFromOutput(output);
            const allParametersCount = _.keys(parameters).length;
            const maxVisibleParameters = 2;
            let parametersCounter = 0;

            output = _.map(parameters, (text, id) => {
                parametersCounter++;

                if (parametersCounter > maxVisibleParameters) {
                    return null;
                }

                return <Chip label={text} key={id} style={{ marginRight: '2px', marginBottom: '2px' }} />;
            });

            if (allParametersCount > maxVisibleParameters) {
                outputAdditional = <span style={{ marginLeft: '2px' }}>+{allParametersCount - maxVisibleParameters}</span>;
            }

            outputTitle = null;
            truncateOutput = false;
        }

        if (truncateOutput) {
            output = _.truncate(output, { length: 48 });
        }

        output = <div>{output}{outputAdditional}</div>;

        if (outputTitle !== null) {
            output = <Tooltip title={outputTitle}>{output}</Tooltip>;
        }

        return [
            <div>{item.id}</div>,
            <div>{item.item_type === 'product'
                ? (_.has(item, 'product') && _.has(item.product, 'translations') && _.has(item.product.translations, 'name')
                    ? <Tooltip title={item.product.translations.name}><span className="item-type-product">{_.truncate(item.product.translations.name, { length: 24 })}</span></Tooltip>
                    : '-')
                : __('Položka')}</div>,
            <div>{status}</div>,
            <div><Tooltip title={item.input}><div>{_.truncate(item.input, { length: 32 })}</div></Tooltip></div>,
            <div className={isError ? 'error-output' : ''}>{output}</div>,
            <div>{formatAmount(item.price, getCurrency(), 8)}</div>,
            <div>
                {this.renderTableButton(
                    item.id,
                    __('Spustiť'),
                    <SmartToyIcon color="orange" />,
                    (callbackLoading, callback) => this.confirmRun(item.id, callbackLoading, callback),
                    { disabled: isRunning }
                )}
                {this.renderTableButton(
                    item.id,
                    __('Upraviť'),
                    <EditIcon />,
                    (callbackLoading, callback) => this.confirmEdit(item.id, item.input, item.output, item.status, callbackLoading, callback),
                    { disabled: isRunning }
                )}
                {this.renderTableButton(
                    item.id,
                    __('Zobraziť odhad ceny'),
                    <AttachMoneyIcon />,
                    (callbackLoading, callback) => this.confirmPrice(item.id, item.input, callbackLoading, callback),
                    { disabled: isProcessed }
                )}
                {item.item_type !== null && item.item_type !== '' ? this.renderTableButton(
                    item.id,
                    isErrorSave ? item.error_save : (isSaved ? __('Uložené v eshope') : __('Uložiť do eshopu')),
                    <SaveIcon color={isErrorSave ? 'error' : (isSaved ? 'secondary' : 'inherit')} />,
                    (callbackLoading, callback) => this.showLightbox('save', { id: item.id, save_field: '', meta_name: '', callbackLoading, callback }),
                    { disabled: !isProcessed || item.output === '' || item.output === null }
                ) : null}
                {!isGenerateParametersFunction ? this.renderTableButton(
                    item.id,
                    __('Skopírovať výstup do schránky'),
                    <CopyIcon />,
                    (callbackLoading, callback) => this.copyOutput(item.output),
                    { disabled: !isProcessed }
                ) : null}
                {this.renderTableButton(
                    item.id,
                    __('Zmazať'),
                    <DeleteIcon />,
                    (callbackLoading, callback) => this.confirmDelete(item.id, callbackLoading, callback),
                    { disabled: isRunning }
                )}
            </div>,
        ];
    }

    getTags() {
        const item_type = this.getItemType();

        let translateLanguageTag = null;
        let translateFieldTag = null;

        const { items } = this.props;

        if (
            items.command.slug === AI_TRANSLATE_FUNCTION_SLUG
            && !_.isEmpty(items.command.settings)
            && _.has(items.command.settings, 'language')
        ) {
            const languages = _.has(items.ai_function_variables, 'language') ? items.ai_function_variables.language.options : {};
            const language = items.command.settings.language;

            translateLanguageTag = { name: `${__('Jazyk')}: ${_.has(languages, language) ? languages[language] : language}` };
        }

        if (
            items.command.slug === AI_TRANSLATE_FUNCTION_SLUG
            && !_.isEmpty(items.command.settings)
            && _.has(items.command.settings, 'translate_field')
        ) {
            const translate_field = items.command.settings.translate_field;

            translateFieldTag = { name: `${__('Údaj na preloženie')}: ${_.has(AI_TRANSLATE_INPUTS, translate_field) ? AI_TRANSLATE_INPUTS[translate_field] : translate_field}` };
        }

        const showImportMessage = !_.isEmpty(items.command_import) && _.includes(['waiting', 'processing'], items.command_import.status);

        return [
            translateLanguageTag,
            translateFieldTag,
            item_type !== 'product' && !showImportMessage ? {
                name: __('Nová položka'),
                onClick: () => this.showLightbox('create', {
                    loading: false,
                    input: '',
                }),
                disabled: this.isDemo(),
            } : null,
            showImportMessage ? {
                name: __('Práve prebieha import položiek zo súboru. Budú naimportované do pár minút.'),
                color: 'orange',
            } : null,
            !_.isEmpty(items.command_import) && items.command_import.status === 'error' ? {
                name: __('Položky sa nepodarilo naimportovať, chyba: {ERROR}.').replace('{ERROR}', items.command_import.error !== null ? items.command_import.error : ''),
                color: 'error',
            } : null,
        ];
    }

    getEmptyText() {
        return __('Zatiaľ nemáte žiadne položky');
    }

    getItemType() {
        const { items } = this.props;

        let item_type = '';

        _.each(items.items, item => {
            if (!_.isEmpty(item.item_type)) {
                item_type = item.item_type;
            }
        });

        return item_type;
    }

    getParametersFromOutput(output) {
        const { items } = this.props;

        if (this.memoryCacheParametersForAi === null) {
            this.memoryCacheParametersForAi = _.reduce(items.parameters_for_ai, (result, values, parameterName) => {
                let parameterValues = {};

                _.each(values, (valueName, id) => {
                    parameterValues = { ...parameterValues, [id]: `${parameterName}: ${valueName}` };
                });

                return { ...result, ...parameterValues };
            }, {});
        }

        const parsedOutput = this.parseOutputJson(output);
        let parameters = {};

        _.each(parsedOutput.found, item => {
            if (_.has(this.memoryCacheParametersForAi, item)) {
                parameters = { ...parameters, [item]: this.memoryCacheParametersForAi[item] };
            }
        });

        return parameters;
    }

    parseOutputJson(output) {
        try {
            output = JSON.parse(output);

            if (_.isObject(output) && !_.isEmpty(output)) {
                return output;
            }
        } catch (e) {}

        return [];
    }

    copyOutput(output) {
        navigator.clipboard.writeText(output);

        this.showSnackbar('success', __('Skopirované do schránky'));
    }

    confirmDelete(id, callbackLoading, callback) {
        this.showLightbox('delete', { id, callbackLoading, callback });
    }

    confirmRun(id, callbackLoading, callback) {
        this.showLightbox('run', { id, callbackLoading, callback });
    }

    confirmEdit(id, input, output, status, callbackLoading, callback) {
        this.showLightbox('edit', { id, input, output, status, callbackLoading, callback });
    }

    confirmPrice(id, input, callbackLoading, callback) {
        this.showLightbox('price', { id, input, type: 'max', loading: false, price: null, callbackLoading, callback });
    }

    onChangeCreate(type, value) {
        const { lightbox } = this.state;

        this.setState({ lightbox: { ...lightbox, create: { ...lightbox.create, [type]: value } } });
    }

    onChangeEdit(type, value) {
        const { lightbox } = this.state;

        this.setState({ lightbox: { ...lightbox, edit: { ...lightbox.edit, [type]: value } } });
    }

    onChangePrice(type, value) {
        const { lightbox } = this.state;

        let additional = {};

        if (type === 'type') {
            additional = { price: null, loading: false };
        }

        this.setState({ lightbox: { ...lightbox, price: { ...lightbox.price, [type]: value, ...additional } } });
    }

    onChangeSave(type, value) {
        const { lightbox } = this.state;

        this.setState({ lightbox: { ...lightbox, save: { ...lightbox.save, [type]: value } } });
    }

    onChangeMultiSave(type, value) {
        const { lightbox } = this.state;

        this.setState({ lightbox: { ...lightbox, multisave: { ...lightbox.multisave, [type]: value } } });
    }

    onChangeMultiEdit(type, value) {
        const { lightbox } = this.state;

        this.setState({ lightbox: { ...lightbox, multiedit: { ...lightbox.multiedit, [type]: value } } });
    }

    onChangeMultiAttachment(type, value) {
        const { lightbox } = this.state;

        this.setState({ lightbox: { ...lightbox, multiattachment: { ...lightbox.multiattachment, [type]: value } } });
    }

    onChangeMultiAttachmentSetting(type, value) {
        const { lightbox } = this.state;

        this.onChangeMultiAttachment('settings', { ...lightbox.multiattachment.settings, [type]: value });
    }

    async delete() {
        const { deleteAiCommandItem, items } = this.props;
        const { lightbox } = this.state;

        // Zavolame loading
        lightbox.delete.callbackLoading();

        // Zavrieme lightbox
        this.closeLightbox('delete');

        // Zmazeme
        await deleteAiCommandItem(this, lightbox.delete.id);

        // Zavolame callback
        lightbox.delete.callback();

        // Znovu nacitame zoznam od prvej stranky
        this.fetchList(1, 0, items.filtered);
    }

    async run() {
        const { runAiCommandItem } = this.props;
        const { lightbox } = this.state;

        // Zavolame loading
        lightbox.run.callbackLoading();

        // Zavrieme lightbox
        this.closeLightbox('run');

        // Zmazeme
        await runAiCommandItem(this, lightbox.run.id);

        // Zavolame callback
        lightbox.run.callback();
    }

    async edit() {
        const { editAiCommandItem } = this.props;
        const { lightbox } = this.state;

        // Zavolame loading
        lightbox.edit.callbackLoading();

        // Zavrieme lightbox
        this.closeLightbox('edit');

        // Zmazeme
        await editAiCommandItem(this, lightbox.edit.id, lightbox.edit.input, lightbox.edit.output);

        // Zavolame callback
        lightbox.edit.callback();
    }

    async create() {
        const { params } = this.props;
        const { lightbox } = this.state;

        if (isEmptyString(lightbox.create.input)) {
            this.showSnackbar('error', __('Nie je vyplnený vstup'));
            return;
        }

        this.onChangeCreate('loading', true);

        request(`/ai-command-items/add/${params.id}`, { input: lightbox.create.input }, 'POST').then(response => {
            const { status } = response.data;

            if (status === 'error') {
                this.onChangeCreate('loading', false);
                this.showSnackbar('error', __('Položku sa nepodarilo pridať'));
                return;
            }

            // Refreshneme
            window.location = window.location.pathname;
        });
    }

    async price() {
        const { lightbox } = this.state;

        this.onChangePrice('loading', true);

        let input = lightbox.price.input;
        let output = '';

        if (lightbox.price.type === 'same') {
            output = input;
        }

        request(`/ai-command-items/getPrice/${lightbox.price.id}`, { input, output }, 'POST').then(response => {
            const { status, data } = response.data;

            if (status === 'error') {
                this.onChangePrice('loading', false);
                this.showSnackbar('error', __('Cenu sa nepodarilo vypočítať'));
                return;
            }

            this.onChangePrice('price', data.total_price);
        });
    }

    async multiDelete() {
        const { deleteAiCommandItems, items, params } = this.props;
        const { lightbox } = this.state;

        // Zavrieme lightbox
        this.closeLightbox('multidelete');

        // Zobrazime loading
        lightbox.multidelete.callbackLoading();

        await deleteAiCommandItems(this, params.id, lightbox.multidelete.ids, items.filtered);

        // Zavolame callback
        lightbox.multidelete.callback();

        // Znovu nacitame zoznam od prvej stranky
        this.fetchList(1, 0, items.filtered);
    }

    async multiRun() {
        const { runAiCommandItems, items, params } = this.props;
        const { lightbox } = this.state;

        // Zavrieme lightbox
        this.closeLightbox('multirun');

        // Zobrazime loading
        lightbox.multirun.callbackLoading();

        await runAiCommandItems(this, params.id, lightbox.multirun.ids, items.filtered);

        // Zavolame callback
        lightbox.multirun.callback();
    }

    async multiEdit() {
        const { editAiCommandItems, items, params } = this.props;
        const { lightbox } = this.state;

        // Zavrieme lightbox
        this.closeLightbox('multiedit');

        // Zobrazime loading
        lightbox.multiedit.callbackLoading();

        await editAiCommandItems(this, params.id, lightbox.multiedit.ids, lightbox.multiedit.input, lightbox.multiedit.output, items.filtered);

        // Zavolame callback
        lightbox.multiedit.callback();
    }

    async multiExport() {
        const { items, params } = this.props;
        const { lightbox } = this.state;

        // Zavrieme lightbox
        this.closeLightbox('multiexport');

        // Zobrazime loading
        lightbox.multiexport.callbackLoading();

        let requestParams = {};

        // Nasetujeme filter
        _.forEach(items.filtered, (value, key) => {
            requestParams = { ...requestParams, ...{ [`filter_${key}`]: value } };
        });

        await request(`/ai-command-items/multiExport/${params.id}/${lightbox.multiexport.ids}`, requestParams).then(response => {
            const { status, data } = response.data;

            if (status === 'error') {
                this.showSnackbar('error', __('Nepodarilo sa stiahnuť export'));
                return;
            }

            // Stiahneme subor
            window.location = data.link;
        });

        // Zavolame callback
        lightbox.multiexport.callback();
    }

    async multiAttachment() {
        const { items, params } = this.props;
        const { lightbox } = this.state;

        // Zavrieme lightbox
        this.closeLightbox('multiattachment');

        // Zobrazime loading
        lightbox.multiattachment.callbackLoading();

        let requestParams = {};

        // Nasetujeme filter
        _.forEach(items.filtered, (value, key) => {
            requestParams = { ...requestParams, ...{ [`filter_${key}`]: value } };
        });

        const settings = lightbox.multiattachment.settings;

        await request(`/ai-command-items/multiAttachment/${params.id}/${lightbox.multiattachment.ids}`, { settings }, 'POST', requestParams).then(response => {
            const { status, data } = response.data;

            if (status === 'error') {
                this.showSnackbar('error', __('Nepodarilo sa stiahnuť export'));
                return;
            }

            // Stiahneme subor
            window.location = data.link;
        });

        // Zavolame callback
        lightbox.multiattachment.callback();
    }

    async multiSave() {
        const { saveAiCommandItems, items, params } = this.props;
        const { lightbox } = this.state;

        const showProductParametersOutput = this.isGenerateParametersFunction();
        let saveField = lightbox.multisave.save_field;

        if (showProductParametersOutput) {
            saveField = 'parameters';
        }

        if (saveField === '') {
            this.showSnackbar('error', __('Nie je vybraný údaj v eshope'));
            return;
        } else if (saveField.toString().indexOf('{meta_key}') !== -1 && lightbox.multisave.meta_name === '') {
            this.showSnackbar('error', __('Nie je vyplnený názov meta'));
            return;
        }

        const save_field = saveField.replace('{meta_key}', lightbox.multisave.meta_name);

        // Zavrieme lightbox
        this.closeLightbox('multisave');

        // Zobrazime loading
        lightbox.multisave.callbackLoading();

        await saveAiCommandItems(this, params.id, save_field, lightbox.multisave.ids, items.filtered);

        // Zavolame callback
        lightbox.multisave.callback();
    }

    async save() {
        const { saveAiCommandItems, params } = this.props;
        const { lightbox } = this.state;

        const showProductParametersOutput = this.isGenerateParametersFunction();
        let saveField = lightbox.save.save_field;

        if (showProductParametersOutput) {
            saveField = 'parameters';
        }

        if (saveField === '') {
            this.showSnackbar('error', __('Nie je vybraný údaj v eshope'));
            return;
        } else if (saveField.toString().indexOf('{meta_key}') !== -1 && lightbox.save.meta_name === '') {
            this.showSnackbar('error', __('Nie je vyplnený názov meta'));
            return;
        }

        const save_field = saveField.replace('{meta_key}', lightbox.save.meta_name);

        // Zavrieme lightbox
        this.closeLightbox('save');

        // Zobrazime loading
        lightbox.save.callbackLoading();

        await saveAiCommandItems(this, params.id, save_field, lightbox.save.id);

        // Zavolame callback
        lightbox.save.callback();
    }

    onAddOutputParameter(id) {
        const { lightbox } = this.state;

        let parsedOutput = this.parseOutputJson(lightbox.edit.output);
        let newParameters = [];

        _.each(parsedOutput.found, parameterId => {
            if (parameterId.toString() !== id.toString()) {
                newParameters = [ ...newParameters, parameterId ];
            }
        });

        newParameters = [ ...newParameters, id ];

        this.onChangeEdit('output', JSON.stringify({ ...parsedOutput, found: newParameters }));
    }

    onDeleteOutputParameter(id) {
        const { lightbox } = this.state;

        let parsedOutput = this.parseOutputJson(lightbox.edit.output);
        let newParameters = [];

        _.each(parsedOutput.found, parameterId => {
            if (parameterId.toString() !== id.toString()) {
                newParameters = [ ...newParameters, parameterId ];
            }
        });

        this.onChangeEdit('output', JSON.stringify({ ...parsedOutput, found: newParameters }));
    }

    isGenerateParametersFunction() {
        const { items } = this.props;

        return items.command.slug === 'GenerateProductParametersByEan';
    }

    getExportColumns(extension) {
        return extension === 'csv' ? this.csvColumns : this.excelColumns;
    }

    renderEditLightbox() {
        const { lightbox } = this.state;

        const showProductParametersOutput = this.isGenerateParametersFunction();
        let parametersOutput = null;
        let parametersNotFound = null;
        let showHtmlInputEditor = false;
        let showHtmlOutputEditor = false;

        if (
            lightbox.edit.input !== null
            && lightbox.edit.input.toString().indexOf('<p>') !== -1
        ) {
            showHtmlInputEditor = true;
        }

        if (showProductParametersOutput) {
            const parameters = this.getParametersFromOutput(lightbox.edit.output);

            parametersOutput = _.map(parameters, (text, id) => {
                return <Chip
                    onDelete={() => this.onDeleteOutputParameter(id)}
                    label={text}
                    key={id}
                />;
            });

            const parsedOutput = this.parseOutputJson(lightbox.edit.output);

            if (_.has(parsedOutput, 'not_found')) {
                parametersNotFound = _.map(parsedOutput.not_found, (item) => {
                    return <Chip
                        label={`${item.name}: ${item.value}`}
                        key={item.name}
                        color="error"
                    />;
                });
            }
        } else if (
            lightbox.edit.output !== null
            && lightbox.edit.output.toString().indexOf('<p>') !== -1
        ) {
            showHtmlOutputEditor = true;
        }

        return (
            <div className={`edit-ai-command-item ${showHtmlOutputEditor ? 'edit-ai-command-item-html' : ''}`}>
                {!showHtmlInputEditor ? <Input
                    label={__('Vstup')}
                    value={lightbox.edit.input}
                    onChange={value => this.onChangeEdit('input', value)}
                    multiline
                /> : <div className="input">
                    <div className="input__label">{__('Vstup')}</div>
                    <CKEditor
                        editor={Editor}
                        data={lightbox.edit.input}
                        onChange={(event, editor) => this.onChangeEdit('input', editor.getData())}
                    />
                </div>}
                {!showProductParametersOutput && !showHtmlOutputEditor ? <Input
                    label={__('Výstup')}
                    value={lightbox.edit.output}
                    onChange={value => this.onChangeEdit('output', value)}
                    disabled={lightbox.edit.status === 'draft'}
                    multiline
                /> : null}
                {showHtmlOutputEditor ? <div className="input">
                    <div className="input__label">{__('Výstup')}</div>
                    <CKEditor
                        editor={Editor}
                        data={lightbox.edit.output}
                        onChange={(event, editor) => this.onChangeEdit('output', editor.getData())}
                    />
                </div> : null}
                {showProductParametersOutput ? <div className="edit-ai-command-item__parameters">
                    {parametersNotFound !== null ? <div className="edit-ai-command-item__parameters__label input__label">{__('Nezaradené parametre')}</div> : null}
                    {parametersNotFound !== null ? <div className="edit-ai-command-item__parameters__items edit-ai-command-item__parameters__items-not-found">{parametersNotFound}</div> : null}
                    <div className="edit-ai-command-item__parameters__label input__label">{__('Výstup')}</div>
                    <div className="edit-ai-command-item__parameters__items">{parametersOutput}</div>
                    {!_.isEmpty(this.memoryCacheParametersForAi) ? <Input
                        label={__('Vlastnosť')}
                        content={<Autocomplete
                            options={_.reduce(this.memoryCacheParametersForAi, (result, name, id) => { return [ ...result, { id, name } ]; }, [])}
                            getOptionLabel={option => option.name}
                            onChange={(event, value) => {
                                if (value !== null) {
                                    this.onAddOutputParameter(value.id);
                                }
                            }}
                            renderInput={(params) => <TextField
                                { ...params }
                                placeholder={__('Začnite písať názov...')}
                                variant="outlined"
                            />}
                            noOptionsText={__('Nenašla sa žiadna vlastnosť')}
                            clearText={__('Zrušiť')}
                        />}
                    /> : null}
                </div> : null}
            </div>
        );
    }

    renderScreen() {
        const { items } = this.props;
        const { lightbox } = this.state;

        const save_fields = _.reduce(items.save_fields, (result, save_field) => {
            let save_field_name = save_field;
            const language_field = /^([a-z]{2})\.(.*)$/.exec(save_field);

            if (
                language_field !== null
                && language_field.length === 3
                && _.has(this.saveFields, language_field[2])
            ) {
                save_field_name = `${this.saveFields[language_field[2]]} (${language_field[1]})`;
            }

            return { ...result, [save_field]: save_field_name };
        }, {});

        const isGenerateParametersFunction = this.isGenerateParametersFunction();

        const showHtmlInputEditor = !_.isEmpty(lightbox.edit)
            && lightbox.edit.input !== null
            && lightbox.edit.input.toString().indexOf('<p>') !== -1;

        const showHtmlOutputEditor = !_.isEmpty(lightbox.edit)
            && lightbox.edit.output !== null
            && lightbox.edit.output.toString().indexOf('<p>') !== -1;

        return (
            <div>
                {this.renderLightbox(
                    'price',
                    __('Odhad ceny položky'),
                    !_.isEmpty(lightbox.price) ? (<div className="price-ai-command-item">
                        <Select
                            label={__('Výpočet odhadovaného výstupu')}
                            options={{ max: __('Maximálny počet tokenov - ideálne pre väčšinu funkcií'), same: __('Rovnaký počet tokenov ako vstup - ideálne pre preklady') }}
                            value={lightbox.price.type}
                            onChange={value => this.onChangePrice('type', value)}
                            allowEmpty={false}
                        />
                        {lightbox.price.price === null ? <Button
                            onClick={() => this.price()}
                            loading={lightbox.price.loading}
                        >{__('Prepočítať')}</Button> : <div className="price-ai-command-item__price">
                            <span>{__('Odhadovaná cena')}:</span>
                            <span>{lightbox.price.price.toString().replace('.', ',')} {formatCurrency(getCurrency())}</span>
                        </div>}
                    </div>) : null,
                    '',
                    __('Zrušiť'),
                    () => {}
                )}
                {this.renderLightbox(
                    'create',
                    __('Nová položka'),
                    !_.isEmpty(lightbox.create) ? (lightbox.create.loading ? this.renderLoading(20) : <div className="create-ai-command-item">
                        <Input
                            label={__('Vstup')}
                            value={lightbox.create.input}
                            onChange={value => this.onChangeCreate('input', value)}
                            multiline
                        />
                    </div>) : null,
                    __('Uložiť'),
                    __('Zrušiť'),
                    () => this.create()
                )}
                {this.renderLightbox(
                    'edit',
                    __('Úprava položky'),
                    !_.isEmpty(lightbox.edit) ? this.renderEditLightbox() : null,
                    __('Uložiť'),
                    __('Zrušiť'),
                    () => this.edit(),
                    false,
                    null,
                    showHtmlInputEditor || showHtmlOutputEditor ? 'html-lightbox' : ''
                )}
                {this.renderLightbox(
                    'run',
                    __('Naozaj chcete spustiť túto položku?'),
                    null,
                    __('Áno'),
                    __('Nie'),
                    () => this.run()
                )}
                {this.renderLightbox(
                    'delete',
                    __('Naozaj chcete zmazať túto položku?'),
                    null,
                    __('Áno'),
                    __('Nie'),
                    () => this.delete()
                )}
                {this.renderLightbox(
                    'multidelete',
                    __('Naozaj chcete zmazať označené položky?'),
                    null,
                    __('Áno'),
                    __('Nie'),
                    () => this.multiDelete()
                )}
                {this.renderLightbox(
                    'multirun',
                    __('Naozaj chcete spustiť označené položky?'),
                    null,
                    __('Áno'),
                    __('Nie'),
                    () => this.multiRun()
                )}
                {this.renderLightbox(
                    'multiexport',
                    __('Naozaj chcete exportovať označené položky?'),
                    null,
                    __('Áno'),
                    __('Nie'),
                    () => this.multiExport()
                )}
                {this.renderLightbox(
                    'multiattachment',
                    __('Naozaj chcete exportovať označené položky do pôvodného súboru'),
                    !_.isEmpty(lightbox.multiattachment) ? (lightbox.multiattachment.loading ? this.renderLoading(20) : <div>
                        {_.includes(['csv', 'xls'], items.attachment_extension) ? <Select
                            label={__('Názov stĺpca do ktorého sa majú exportovať položky')}
                            options={this.getExportColumns(items.attachment_extension)}
                            value={lightbox.multiattachment.settings.export_cell}
                            onChange={value => this.onChangeMultiAttachmentSetting('export_cell', value)}
                        /> : null}
                    </div>) : null,
                    __('Exportovať'),
                    __('Zrušiť'),
                    () => this.multiAttachment()
                )}
                {this.renderLightbox(
                    'multisave',
                    isGenerateParametersFunction ? __('Naozaj chcete uložiť parametre v eshope?') : __('Uložiť do eshopu'),
                    !_.isEmpty(lightbox.multisave) ? (<div className="save-ai-command-item">
                        {!isGenerateParametersFunction ? <Select
                            label={__('Údaj v eshope')}
                            options={save_fields}
                            value={lightbox.multisave.save_field}
                            onChange={value => this.onChangeMultiSave('save_field', value)}
                        /> : null}
                        {!isGenerateParametersFunction && lightbox.multisave.save_field.toString().indexOf('{meta_key}') !== -1 ? <Input
                            label={__('Názov meta')}
                            value={lightbox.multisave.meta_name}
                            onChange={value => this.onChangeMultiSave('meta_name', value)}
                        /> : null}
                    </div>) : null,
                    __('Uložiť'),
                    __('Zrušiť'),
                    () => this.multiSave()
                )}
                {this.renderLightbox(
                    'save',
                    isGenerateParametersFunction ? __('Naozaj chcete uložiť parametre v eshope?') : __('Uložiť do eshopu'),
                    !_.isEmpty(lightbox.save) ? (<div className="save-ai-command-item">
                        {!isGenerateParametersFunction ? <Select
                            label={__('Údaj v eshope')}
                            options={save_fields}
                            value={lightbox.save.save_field}
                            onChange={value => this.onChangeSave('save_field', value)}
                        /> : null}
                        {!isGenerateParametersFunction && lightbox.save.save_field.toString().indexOf('{meta_key}') !== -1 ? <Input
                            label={__('Názov meta')}
                            value={lightbox.save.meta_name}
                            onChange={value => this.onChangeSave('meta_name', value)}
                        /> : null}
                    </div>) : null,
                    __('Uložiť'),
                    __('Zrušiť'),
                    () => this.save()
                )}
                {this.renderLightbox(
                    'multiedit',
                    __('Naozaj chcete upraviť označené položky?'),
                    !_.isEmpty(lightbox.multiedit) ? (<div className="edit-ai-command-item">
                        <Input
                            label={__('Vstup')}
                            value={lightbox.multiedit.input}
                            onChange={value => this.onChangeMultiEdit('input', value)}
                        />
                        <Input
                            label={__('Výstup')}
                            value={lightbox.multiedit.output}
                            onChange={value => this.onChangeMultiEdit('output', value)}
                        />
                    </div>) : null,
                    __('Áno'),
                    __('Nie'),
                    () => this.multiEdit()
                )}
                {this.renderSnackbar()}
            </div>
        );
    }
}

const stateToProps = ({ ai_command_items, user }) => ({ items: ai_command_items, user });

export default withCookies(connect(stateToProps, {
    fetch: fetchAiCommandItems,
    clean: cleanAiCommandItems,
    setUser,
    changeUserSetting,
    deleteAiCommandItem,
    deleteAiCommandItems,
    runAiCommandItem,
    runAiCommandItems,
    editAiCommandItem,
    editAiCommandItems,
    saveAiCommandItems,
})(AiCommandItemsScreen));
